// components/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, ChevronRight, X, Check, Clock, AlertCircle, Info, CheckCircle, AlertTriangle, Maximize, Minimize, ChevronLeft } from 'lucide-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { fetchAdminProfile } from './AdminData';

const BREADCRUMBS = [
  // Dashboard
  {
    path: "/admin/dashboard",
    items: ["Dashboard"],
  },

  // Staff Profile
  {
    path: '/admin/staff-profile',
    items: ["Staff Profile"]
  },

  // Masters
  {
    path: "/admin/masters/all",
    items: ["Masters"],
  },
  {
    path: "/admin/masters/active",
    items: ["Active Masters"],
  },
  {
    path: "/admin/masters/inactive",
    items: ["Inactive Masters"],
  },
  {
    path: "/admin/master/view/:id",
    items: ["Masters", "Single Master Details"],
  },

  //Users

  {
    path: "/admin/users/all",
    items: ["Users"],
  },
  {
    path: "/admin/users/active",
    items: ["Active Users"],
  },
  {
    path: "/admin/users/inactive",
    items: ["Inactive Users"],
  },
  {
    path: "/admin/user/view/:id",
    items: ["Users", "Single User Details"],
  },

  // Plans
  {
    path: "/admin/plans",
    items: ["Plans"],
  },
  {
    path: "/admin/plans/new",
    items: ["Plans", "Add Plan"],
  },
  {
    path: "/admin/plans/:id/edit",
    items: ["Plans", "Edit Plan"],
  },
  {
    path: "/admin/plans/:planId/active-users",
    items: ["Plans", "Active Plan Users"],
  },
  {
    path: "/admin/plans/:planId/inactive-users",
    items: ["Plans", "Inactive Plan Users"],
  },
  {
    path: "/admin/plans/:id",
    items: ["Plans", "Single Plan Details"],
  },

  // Other pages
  {
    path: "/admin/analytics",
    items: ["Analytics"],
  },
  {
    path: "/admin/complaints/all",
    items: ["All Complaints"],
  },
  {
    path: "/admin/complaints/complaint-types",
    items: ["Complaints Types"],
  },
  {
    path: "/admin/complaints/pending",
    items: ["Pending Complaints"],
  },
  {
    path: "/admin/complaints/in-progress",
    items: ["In Progress Complaints"],
  },
  {
    path: "/admin/complaints/resolved",
    items: ["Resolved Complaints"],
  },
  {
    path: "/admin/notifications",
    items: ["Notifications"],
  },

  // Roles
  {
    path: "/admin/staff",
    items: ["Staff Management", "All Staff"],
  },
  {
    path: "/admin/staff/add",
    items: ["Staff Management", "Add Staff"],
  },
  {
    path: "/admin/staff/add/:id",
    items: ["Staff Management", "Edit Staff"],
  },
  {
    path: "/admin/staff/:id",
    items: ["Staff Management", "Single Staff Details"],
  },

  // Department
  {
    path: "/admin/departments",
    items: ["Department Management"],
  },

  // Staff
  {
    path: "/admin/staff",
    items: ["Staff Management", "All Staff"],
  },
  {
    path: "/admin/staff/add",
    items: ["Staff Management", "Add Staff"],
  },
  {
    path: "/admin/staff/add/:id",
    items: ["Staff Management", "Edit Staff"],
  },
  {
    path: "/admin/staff/:id",
    items: ["Staff Management", "Single Staff Details"],
  },

  // Settings
  {
    path: "/admin/profile",
    items: ["Settings", "Admin Profile"],
  },
  {
    path: "/admin/settings",
    items: ["Settings", "Terms & Policies"],
  },
];

// ── Notification Service ──────────────────────────────────────────────────────
const NotificationService = {
  getAll: () => {
    const stored = localStorage.getItem('notifications');
    if (stored) return JSON.parse(stored);
    const defaultNotifications = [
      {
        id: 1,
        title: "Security Alert: Unauthorized Access Attempt",
        message: "Multiple failed login attempts detected from IP 192.168.1.100.",
        type: "alert",
        priority: "high",
        status: "unread",
        createdAt: new Date(Date.now() - 5 * 60000).toISOString(),
        category: "security"
      },
      {
        id: 2,
        title: "New Device Enrollment Request",
        message: "iPhone 14 Pro requests enrollment from user Raj Mehta.",
        type: "info",
        priority: "medium",
        status: "unread",
        createdAt: new Date(Date.now() - 15 * 60000).toISOString(),
        category: "device"
      },
      {
        id: 3,
        title: "System Update Available",
        message: "MDM Server v3.2.0 is now available with security improvements.",
        type: "info",
        priority: "low",
        status: "read",
        createdAt: new Date(Date.now() - 2 * 3600000).toISOString(),
        category: "system"
      },
      {
        id: 4,
        title: "Compliance Violation Detected",
        message: "7 devices are out of compliance with security policies.",
        type: "alert",
        priority: "high",
        status: "unread",
        createdAt: new Date(Date.now() - 45 * 60000).toISOString(),
        category: "compliance"
      },
      {
        id: 5,
        title: "Weekly Analytics Report Available",
        message: "Your weekly device analytics report is ready for review.",
        type: "success",
        priority: "low",
        status: "unread",
        createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
        category: "analytics"
      }
    ];
    localStorage.setItem('notifications', JSON.stringify(defaultNotifications));
    return defaultNotifications;
  },

  markAsRead: (id) => {
    const notifications = NotificationService.getAll();
    const index = notifications.findIndex(n => n.id === id);
    if (index === -1) return;
    notifications[index].status = 'read';
    notifications[index].readAt = new Date().toISOString();
    localStorage.setItem('notifications', JSON.stringify(notifications));
    return notifications[index];
  },

  markAllAsRead: () => {
    const notifications = NotificationService.getAll();
    const updated = notifications.map(n => ({ ...n, status: 'read', readAt: new Date().toISOString() }));
    localStorage.setItem('notifications', JSON.stringify(updated));
    return updated;
  },

  delete: (id) => {
    const notifications = NotificationService.getAll();
    const filtered = notifications.filter(n => n.id !== id);
    localStorage.setItem('notifications', JSON.stringify(filtered));
    return filtered;
  },

  getUnreadCount: () => {
    const notifications = NotificationService.getAll();
    return notifications.filter(n => n.status === 'unread').length;
  }
};

// ── Notification Icon Helper ──────────────────────────────────────────────────
const getNotificationIcon = (type) => {
  const configs = {
    alert: { icon: AlertCircle, color: '#FF7D38', bg: 'rgba(255,125,56,0.12)' },
    warning: { icon: AlertTriangle, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    info: { icon: Info, color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    success: { icon: CheckCircle, color: '#34d399', bg: 'rgba(52,211,153,0.12)' }
  };
  return configs[type] || configs.info;
};

// ── Format Time Helper ──────────────────────────────────────────────────────
const formatTime = (dateString) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

// ── Notification Dropdown Component ──────────────────────────────────────────
const NotificationDropdown = ({ isOpen, onClose, onNotificationCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  const loadNotifications = () => {
    setLoading(true);
    setTimeout(() => {
      const data = NotificationService.getAll();
      setNotifications(data);
      setLoading(false);
    }, 200);
  };

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleMarkAsRead = (id) => {
    const updated = NotificationService.markAsRead(id);
    if (updated) {
      setNotifications(prev => prev.map(n => n.id === updated.id ? updated : n));
      onNotificationCountChange();
    }
  };

  const handleMarkAllAsRead = () => {
    const updated = NotificationService.markAllAsRead();
    setNotifications(updated);
    onNotificationCountChange();
  };

  const handleDelete = (id) => {
    const remaining = NotificationService.delete(id);
    setNotifications(remaining);
    onNotificationCountChange();
  };

  const unreadCount = notifications.filter(n => n.status === 'unread').length;
  const recentNotifications = notifications.slice(0, 5);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-80 max-h-[480px] bg-white rounded-2xl shadow-2xl border border-orange-200 overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-orange-100 bg-orange-50">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-orange-500" />
          <span className="font-semibold text-gray-800">Notifications</span>
          {unreadCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-500 text-white">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs font-medium text-orange-500 hover:text-orange-600 transition-colors"
            >
              Mark all read
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-orange-100 transition-colors text-gray-400">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto max-h-[380px]">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-orange-400 border-t-transparent" />
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">No notifications</p>
            <p className="text-xs text-gray-400">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-orange-100">
            {recentNotifications.map((notification) => {
              const iconConfig = getNotificationIcon(notification.type);
              const Icon = iconConfig.icon;
              const isUnread = notification.status === 'unread';

              return (
                <div
                  key={notification.id}
                  className={`px-4 py-3 transition-colors hover:bg-orange-50 cursor-pointer ${isUnread ? 'bg-orange-50/50' : ''
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="p-1.5 rounded-lg shrink-0 mt-0.5"
                      style={{ background: iconConfig.bg }}
                    >
                      <Icon size={14} style={{ color: iconConfig.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs font-medium ${isUnread ? 'text-gray-800' : 'text-gray-600'}`}>
                          {notification.title}
                        </p>
                        {isUnread && (
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                          <Clock size={10} />
                          {formatTime(notification.createdAt)}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full capitalize bg-orange-50 text-orange-500 border border-orange-200">
                          {notification.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        {isUnread && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification.id);
                            }}
                            className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-500 border border-blue-200 hover:bg-blue-100 transition-colors"
                          >
                            Mark as read
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification.id);
                          }}
                          className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2.5 border-t border-orange-100 bg-gray-50">
          <button
            onClick={() => {
              onClose();
              window.location.href = '/admin/notifications';
            }}
            className="w-full text-center text-xs font-medium text-orange-500 hover:text-orange-600 transition-colors"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Navbar Component ──────────────────────────────────────────────────────
export const Navbar = ({ setOpen, activePage, sidebarOpen }) => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDot, setShowDot] = useState(true);

  const [userRole, setUserRole] = useState("");
  const [userName, setUserName] = useState("");
  const [userAvatar, setUserAvatar] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;

  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAdminData = async () => {
      const result = await fetchAdminProfile();

      if (result.success) {
        setAdminData(result.data);
      } else {
        // console.error("Failed to get admin data:", result.message);
      }

      setLoading(false);
    };

    // Initial API call
    loadAdminData();

    // Refresh when profile is updated
    const handleProfileUpdated = () => {
      loadAdminData();
    };

    window.addEventListener(
      "adminProfileUpdated",
      handleProfileUpdated
    );

    return () => {
      window.removeEventListener(
        "adminProfileUpdated",
        handleProfileUpdated
      );
    };
  }, []);

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  const breadcrumb =
    BREADCRUMBS.find((item) =>
      matchPath(
        {
          path: item.path,
          end: true,
        },
        currentPath
      )
    )?.items || ["Dashboard"];

  const updateUnreadCount = () => {
    const count = NotificationService.getUnreadCount();
    setUnreadCount(count);
    setShowDot(count > 0);
  };

  useEffect(() => {
    updateUnreadCount();
    const handleStorageChange = () => {
      updateUnreadCount();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    const adminToken = sessionStorage.getItem("adminToken");
    const subAdminAuth = sessionStorage.getItem("subAdminAuth");
    const staffData = sessionStorage.getItem("subAdminData");

    if (adminToken && adminAuth === "true" && adminData) {
      setUserRole(adminData.role || "Super Admin");
      setUserName(adminData.name || "Super Admin");
      setUserAvatar(adminData.image || "");
    }
    else if (subAdminAuth === "true" && staffData) {
      const staff = JSON.parse(staffData);

      setUserRole(staff.role || "Staff");
      setUserName(staff.name || "Staff");
      setUserAvatar(staff.image || "");
    }
  }, [adminData]);

  const toggleNotification = () => {
    setIsNotificationOpen(!isNotificationOpen);
    if (isNotificationOpen) {
      updateUnreadCount();
    }
  };

  const handleAvatarClick = () => {
    if (userRole === "superAdmin") {
      navigate("/admin/profile");
    } else {
      navigate("/admin/staff-profile");
    }
  };

  // Toggle sidebar open/close — works on mobile (drawer) and desktop (collapse)
  const handleSidebarToggle = () => {
    setOpen((prev) => !prev);
  };

  return (
    <header className="flex items-center px-4 gap-4 shrink-0 sticky top-0 z-10 h-16 bg-white border-b border-orange-200/30 shadow-sm">
      {/* Menu toggle — visible on all screen sizes now */}
      <button
        className="p-1.5 rounded-lg transition-colors text-orange-500 bg-orange-50 border border-orange-300/50"
        onClick={handleSidebarToggle}
        aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {sidebarOpen ? <Menu size={20} /> : <Menu size={20} />}
      </button>

      {/* Breadcrumb */}
      <div className="hidden md:flex items-center gap-2 text-sm">
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item}>
            {index > 0 && (
              <ChevronRight
                size={14}
                className="text-orange-400"
              />
            )}

            <span
              className={
                index === breadcrumb.length - 1
                  ? "bg-orange-100/50 px-3 py-1.5 rounded-lg border border-orange-300/50 text-xs md:text-sm font-bold shadow-sm"
                  : "text-gray-500 text-xs md:text-sm font-medium"
              }
            >
              {item}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-2">

        {/* Full Screen / Minimize */}
        <button
          onClick={toggleFullscreen}
          className="p-2 rounded-xl transition-all bg-orange-50/50 border border-orange-200/30 text-orange-500 hover:bg-orange-100/50 hover:border-orange-300/50"
          title={isFullscreen ? "Minimize" : "Maximize"}
        >
          {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        </button>

        {/* Notification bell */}
        <div className="relative">
          <button
            onClick={toggleNotification}
            className="relative p-2 rounded-xl transition-all bg-orange-50/50 border border-orange-200/30 text-orange-500 hover:bg-orange-100/50 hover:border-orange-300/50"
          >
            <Bell size={16} />
            {showDot && (
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse" />
            )}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 text-[9px] font-bold text-white bg-red-500 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-lg shadow-red-500/30">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          <NotificationDropdown
            isOpen={isNotificationOpen}
            onClose={() => {
              setIsNotificationOpen(false);
              updateUnreadCount();
            }}
            onNotificationCountChange={updateUnreadCount}
          />
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden transition-all bg-gradient-to-br from-orange-500 to-orange-600 border border-orange-300/50 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/40 hover:border-orange-400/70">
            <div onClick={handleAvatarClick} className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden transition-all bg-gradient-to-br from-orange-500 to-orange-600 border border-orange-300/50 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/40 hover:border-orange-400/70">
              {userAvatar ? (
                <img
                  src={userAvatar}
                  className="w-full h-full object-cover"
                  alt="admin"
                />
              ) : (
                <span className="text-white font-semibold text-sm">
                  A
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-800">
              {userName}
            </span>

            <span className="text-xs text-orange-500">
              {userRole}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};